import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, Legend 
} from 'recharts';
import { TrendingUp, FileText, PhoneCall, Award, Users, CreditCard, Activity, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981'];
const CHART_COLORS = {
  primary: '#4338CA',
  secondary: '#8B5CF6',
  tertiary: '#06B6D4',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444'
};

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [scoreDistribution, setScoreDistribution] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [dailyActivity, setDailyActivity] = useState([]);
  const [creditUsage, setCreditUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, distRes, weeklyRes, agentRes, dailyRes, creditRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/charts/score-distribution`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/charts/weekly-trend`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/charts/agent-performance`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/charts/daily-activity`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/charts/credit-usage`).catch(() => ({ data: [] }))
      ]);
      
      setStats(overviewRes.data);
      setScoreDistribution(distRes.data);
      setWeeklyTrend(weeklyRes.data);
      setAgentPerformance(agentRes.data);
      setDailyActivity(dailyRes.data);
      setCreditUsage(creditRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Calls',
      value: stats?.total_calls || 0,
      icon: PhoneCall,
      color: 'border-blue-200 bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Assessments',
      value: stats?.total_assessments || 0,
      icon: FileText,
      color: 'border-emerald-200 bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    {
      title: 'Avg Score',
      value: `${stats?.average_score || 0}%`,
      icon: Award,
      color: 'border-indigo-200 bg-indigo-50',
      iconColor: 'text-indigo-600',
    },
  ];

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Analytics Dashboard
            </h1>
            <p className="text-slate-600">Track performance metrics and trends</p>
          </div>
          <Button onClick={fetchAllData} variant="outline" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
            Refresh
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className={`p-6 border ${stat.color} shadow-sm`} data-testid={`stat-card-${index}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon size={24} strokeWidth={1.5} className={stat.iconColor} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Charts Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">
              <Activity size={16} className="mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="performance">
              <Users size={16} className="mr-2" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="trends">
              <TrendingUp size={16} className="mr-2" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="credits">
              <CreditCard size={16} className="mr-2" />
              Credits
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Score Distribution Pie Chart */}
              <Card className="p-6 border border-slate-200 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Score Distribution</h3>
                {scoreDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={scoreDistribution}
                        dataKey="count"
                        nameKey="range"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {scoreDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </Card>

              {/* Daily Activity Chart */}
              <Card className="p-6 border border-slate-200 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Daily Activity (Last 14 Days)</h3>
                {dailyActivity.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={dailyActivity}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '11px' }} />
                      <YAxis stroke="#64748B" style={{ fontSize: '11px' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="calls" stackId="1" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary} fillOpacity={0.6} name="Calls Uploaded" />
                      <Area type="monotone" dataKey="assessments" stackId="2" stroke={CHART_COLORS.success} fill={CHART_COLORS.success} fillOpacity={0.6} name="Assessments" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="mt-6 space-y-6">
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Agent Performance (Top 10)</h3>
              {agentPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={agentPerformance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} stroke="#64748B" style={{ fontSize: '11px' }} />
                    <YAxis dataKey="name" type="category" width={120} stroke="#64748B" style={{ fontSize: '11px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                      }}
                      formatter={(value, name) => [
                        name === 'avgScore' ? `${value}%` : value,
                        name === 'avgScore' ? 'Avg Score' : 'Total Calls'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="avgScore" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} name="Avg Score" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <Users size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No agent performance data available</p>
                    <p className="text-sm">Assign agents to calls and run assessments</p>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="mt-6 space-y-6">
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Weekly Assessment Trend (Last 8 Weeks)</h3>
              {weeklyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={weeklyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="week" stroke="#64748B" style={{ fontSize: '11px' }} />
                    <YAxis yAxisId="left" stroke="#64748B" style={{ fontSize: '11px' }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} stroke="#64748B" style={{ fontSize: '11px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="assessments" stroke={CHART_COLORS.primary} strokeWidth={3} dot={{ fill: CHART_COLORS.primary, r: 5 }} name="Assessments" />
                    <Line yAxisId="right" type="monotone" dataKey="avgScore" stroke={CHART_COLORS.success} strokeWidth={3} dot={{ fill: CHART_COLORS.success, r: 5 }} name="Avg Score %" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  No trend data available
                </div>
              )}
            </Card>

            {/* Recent Scores Line Chart */}
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Assessment Scores</h3>
              {stats?.recent_assessments?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.recent_assessments.slice(0, 10).reverse().map((a, i) => ({
                    name: `#${i + 1}`,
                    score: a.overall_score
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: '11px' }} />
                    <YAxis domain={[0, 100]} stroke="#64748B" style={{ fontSize: '11px' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke={CHART_COLORS.secondary} strokeWidth={3} dot={{ fill: CHART_COLORS.secondary, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No assessment data available
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Credits Tab */}
          <TabsContent value="credits" className="mt-6 space-y-6">
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Credit Usage (Last 30 Days)</h3>
              {creditUsage.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={creditUsage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '10px' }} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#64748B" style={{ fontSize: '11px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="credits" fill={CHART_COLORS.warning} radius={[4, 4, 0, 0]} name="Credits Used" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <CreditCard size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No credit usage data available</p>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Recent Assessments Table */}
        {stats?.recent_assessments && stats.recent_assessments.length > 0 && (
          <Card className="p-6 border border-slate-200 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Assessments</h3>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recent-assessments-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Call ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Date</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_assessments.slice(0, 10).map((assessment) => (
                    <tr key={assessment.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-700 font-mono">
                        {assessment.call_id.slice(0, 12)}...
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {new Date(assessment.assessed_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span
                          className={`font-bold px-2 py-1 rounded ${
                            assessment.overall_score >= 80
                              ? 'bg-emerald-100 text-emerald-700'
                              : assessment.overall_score >= 60
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-rose-100 text-rose-700'
                          }`}
                        >
                          {assessment.overall_score}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
